import { useState } from 'react';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import { StyledButton } from 'components/Buttons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import * as D from '../../InvoiceLinesPanel.css';

type Props = {
  open: boolean;
  originalExternalName: string;
  initialSelectedIndices: number[];
  onSave: (selectedIndices: number[]) => void;
  onClose: () => void;
};

const WordsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem 0;
`;

const WordButton = styled.button<{ $selected: boolean }>`
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  border: 1px solid ${({ $selected }) => ($selected ? '#212121' : '#c0c0c0')};
  background-color: ${({ $selected }) =>
    $selected ? '#C4DBFF' : 'transparent'};
  color: ${({ $selected }) => ($selected ? '#212121' : '#c0c0c0')};
  cursor: pointer;
  font-size: 0.875rem;
  transition: border-color 0.15s, background-color 0.15s, color 0.15s;

  &:hover {
    background-color: ${({ $selected }) => ($selected ? '#B3CDF5' : '#f5f5f5')};
  }
`;

export const KeywordSelectorDialog = ({
  open,
  originalExternalName,
  initialSelectedIndices,
  onSave,
  onClose,
}: Props) => {
  const { t } = useTranslation(['misc', 'invoice']);
  const words = originalExternalName.split(' ');

  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    () => new Set(initialSelectedIndices),
  );

  const toggleWord = (index: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleSave = () => {
    onSave(Array.from(selectedIndices).sort((a, b) => a - b));
  };

  return (
    <D.StyledDialog open={open} onClose={onClose} maxWidth='lg'>
      <DialogTitle>{t('selectKeywords', { ns: 'invoice' })}</DialogTitle>
      <Divider />
      <DialogContent>
        <WordsContainer>
          {words.map((word, index) => (
            <WordButton
              key={index}
              $selected={selectedIndices.has(index)}
              onClick={() => toggleWord(index)}
              type='button'
            >
              {word}
            </WordButton>
          ))}
        </WordsContainer>
      </DialogContent>
      <Divider />
      <DialogActions>
        <StyledButton $primary onClick={handleSave}>
          {t('button.save', { ns: 'misc' })}
        </StyledButton>
        <StyledButton onClick={onClose}>
          {t('button.cancel', { ns: 'misc' })}
        </StyledButton>
      </DialogActions>
    </D.StyledDialog>
  );
};
